import { defaultLanguage } from 'constants/languages';
import { PageType } from 'constants/pages';
import getPageData from 'strapi/pageMapper';
import { StrapiResponseModel } from 'strapi/types';
import getPageTypeFromSlug from 'utils/getPageTypeFromSlug';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { PageDataState } from './types';

const customEqualityFn = <U>(a: U | StrapiResponseModel, b: U | StrapiResponseModel) => {
  if ((!b && !a) || a === b) {
    return true;
  }
  if (b && typeof b === 'object' && (b as StrapiResponseModel)?.data?.seoSettings) {
    return shallow(
      (a as StrapiResponseModel)?.data?.seoSettings,
      (b as StrapiResponseModel)?.data?.seoSettings,
    );
  }
  if (b && typeof b === 'object' && (b as StrapiResponseModel)?.data?.id) {
    return (a as StrapiResponseModel)?.data?.id === (b as StrapiResponseModel)?.data?.id;
  }

  return shallow(a, b);
};

const isLandingOrTemplate = (pageType: PageType) =>
  pageType === PageType.LandingOrHub || pageType === PageType.Template;

const usePageDataStore = createWithEqualityFn<PageDataState>(
  set => ({
    pageType: PageType.Unknown,
    pageData: null,
    templateData: null,
    isLoggedIn: null,
    setIsLoggedIn: (loggedIn: boolean) => set({ isLoggedIn: loggedIn }),
    setColorData: data => set({ colorData: data, loading: false }),
    setColorPages: data => set({ colorPages: data, loading: false }),
    loading: true,
    needToOpenRegModal: false,
    setNeedToOpenRegModal: (needToOpenRegModal: boolean) => set({ needToOpenRegModal }),

    setPageData: data => set({ pageData: data, loading: false }),

    fetchPageData: async (slug, language, routeQueryParams, isDraft) => {
      const pageType = getPageTypeFromSlug(slug);

      const handleFinalError = error => {
        set({ loading: false });
        if (slug !== 'not-found') {
          console.error(error);
          throw new Error('Page not found');
        }
      };

      const fetchLandingPageData = async locale => {
        const pageRespData = await getPageData({
          pageSlug: encodeURI(slug),
          locale,
          queryParam: routeQueryParams,
          isDraft,
          pageType,
        });
        set({ pageData: pageRespData, loading: false, pageType });
      };

      try {
        // Try get the page data from Landing CMS
        await fetchLandingPageData(language);
      } catch (error) {
        // If the data couldn't be fetched, try with the default locale
        if (language !== defaultLanguage && isLandingOrTemplate(pageType)) {
          try {
            await fetchLandingPageData(defaultLanguage);
          } catch (fallbackError) {
            handleFinalError(fallbackError);
          }
        } else {
          handleFinalError(error);
        }
      }
    },
  }),
  customEqualityFn,
);

export default usePageDataStore;
